import { useState, useEffect, useRef } from 'react';

/* ══════════════════════════════════════════════
   STEP VISUALS
══════════════════════════════════════════════ */

const QR_SPOTS = ['Table 7', 'Room 214', 'Counter B', 'Bay A-03'];

function VisualQR() {
  return (
    <div className="hv-visual hv-visual--qr">
      <div className="hvq-grid-2x2">
        {QR_SPOTS.map(label => (
          <div className="hvq-unit" key={label}>
            <div className="hvq-chip">{label}</div>
            <div className="hvq-frame">
              <div className="hvq-grid">
                {QR_CELLS.map((on, i) => <span key={i} data-on={on ? '1' : undefined} />)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="hvq-hint">One code per location. Printed in seconds.</p>
    </div>
  );
}

function VisualScan() {
  return (
    <div className="hv-visual hv-visual--scan">
      <div className="hvs-phone">
        <div className="hvs-notch" />
        <div className="hvs-screen">
          <div className="hvs-beam" />
          <div className="hvs-qr-target">
            <span /><span /><span /><span />
          </div>
          <p className="hvs-label">Scanning…</p>
        </div>
      </div>
      <div className="hvs-signal">
        <span /><span /><span />
      </div>
    </div>
  );
}

function VisualCart() {
  return (
    <div className="hv-visual hv-visual--cart">
      <div className="hvc-phone">
        <div className="hvc-notch" />
        <div className="hvc-screen">
          <div className="hvc-bar">
            <span className="hvc-title">Shared cart</span>
            <span className="hvc-count">3 items</span>
          </div>
          <div className="hvc-items">
            <div className="hvc-item hvc-item--1">
              <span className="hvc-dot" />
              <span>Wagyu burger</span>
              <span className="hvc-qty">×2</span>
            </div>
            <div className="hvc-item hvc-item--2">
              <span className="hvc-dot" />
              <span>Burrata</span>
              <span className="hvc-qty">×1</span>
            </div>
            <div className="hvc-item hvc-item--3">
              <span className="hvc-dot" />
              <span>House red</span>
              <span className="hvc-qty">×1</span>
            </div>
          </div>
          <div className="hvc-footer">
            <span className="hvc-note">Guest 2 adding…</span>
            <span className="hvc-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}

function VisualOrder() {
  return (
    <div className="hv-visual hv-visual--order">
      <div className="hvo-card">
        <div className="hvo-top">
          <span className="hvo-tag">Your order</span>
          <span className="hvo-check">✓</span>
        </div>
        <div className="hvo-items">
          <div className="hvo-item"><span className="hvo-dot" />Wagyu burger <em>×2</em></div>
          <div className="hvo-item"><span className="hvo-dot" />House red <em>×1</em></div>
          <div className="hvo-item"><span className="hvo-dot" />Burrata <em>×1</em></div>
        </div>
        <div className="hvo-footer">
          <span>Modifier: no onions</span>
          <span className="hvo-total">£68.00</span>
        </div>
      </div>
      <div className="hvo-ping"><span /><span /><span /></div>
    </div>
  );
}

/* Kitchen visual — ticket arrival driven by React state, not CSS animation */
function VisualKitchen() {
  const [arrived, setArrived] = useState(false);

  useEffect(() => {
    /* Wait for panel fade-in (400ms) then land the ticket */
    const t = setTimeout(() => setArrived(true), 480);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="hv-visual hv-visual--kitchen">
      <div className="hvk-bar">
        <span className="hvk-col-label">New</span>
        <span className="hvk-col-label">Active</span>
        <span className="hvk-col-label">Done</span>
      </div>
      <div className="hvk-cols">

        {/* NEW column — incoming ticket */}
        <div className="hvk-col hvk-col--new">
          <div className={`hvk-ticket hvk-ticket--incoming${arrived ? ' hvk-arrived' : ''}`}>
            <div className="hvk-ticket-head">
              <span className="hvk-dot hvk-dot--green" />
              <span>Table 7</span>
            </div>
            <span>Wagyu burger ×2</span>
            <span>House red ×1</span>
          </div>
        </div>

        {/* ACTIVE column */}
        <div className="hvk-col">
          <div className="hvk-ticket">
            <div className="hvk-ticket-head">
              <span className="hvk-dot hvk-dot--amber" />
              <span>Table 3</span>
            </div>
            <span>Sea bass ×1</span>
          </div>
        </div>

        {/* DONE column */}
        <div className="hvk-col">
          <div className="hvk-ticket hvk-ticket--done">
            <div className="hvk-ticket-head">
              <span className="hvk-dot hvk-dot--dim" />
              <span>Table 11</span>
            </div>
            <span>Tiramisu ×2</span>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   STEP DATA — single unified flow
══════════════════════════════════════════════ */

const STEPS = [
  {
    num: '01',
    title: 'QR placed',
    sub: 'One unique code per location — table, counter, car window, or hotel room. Linked to your dashboard automatically.',
    Visual: VisualQR,
  },
  {
    num: '02',
    title: 'Guest scans',
    sub: 'Menu opens in any mobile browser. No app download, no account, no friction — in under 10 seconds.',
    Visual: VisualScan,
  },
  {
    num: '03',
    title: 'Items added to cart',
    sub: 'Guests browse, customise, and add items. Multiple guests at the same location share one cart in real time.',
    Visual: VisualCart,
  },
  {
    num: '04',
    title: 'Order placed',
    sub: 'One tap to place the full order. Modifiers, dietary notes, and payment all handled in the same flow.',
    Visual: VisualOrder,
  },
  {
    num: '05',
    title: 'Ticket fires instantly',
    sub: 'The moment they confirm, the ticket lands in the Live Order Hub tagged to the right table, counter slot, car bay, or room number.',
    Visual: VisualKitchen,
  },
];

const N       = STEPS.length;
const STEP_MS = 5000;
const FADE_MS = 450;

/* ══════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════ */

export default function HowItWorks() {
  /* ── Refs drive the RAF loop (smooth ring + track, no effect re-runs) ── */
  const activeRef  = useRef(0);
  const progRef    = useRef(0);
  const fadingRef  = useRef(false);
  const startRef   = useRef(null);
  const rafRef     = useRef(null);
  const resetTmRef = useRef(null);
  const cycleRef   = useRef(0);

  /* ── React state drives content + node classes (instant, no batching lag) ── */
  const [active, setActive]   = useState(0);
  const [cycle,  setCycle]    = useState(0);
  const [fading, setFading]   = useState(false);

  /* setTick is only for the ring + track — reads progRef every frame */
  const [, setTick] = useState(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(resetTmRef.current);
    activeRef.current = 0;
    progRef.current   = 0;
    fadingRef.current = false;
    cycleRef.current  = 0;
    setActive(0);
    setCycle(0);
    setFading(false);
    startRef.current  = performance.now();

    const loop = (now) => {
      if (fadingRef.current) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const elapsed   = now - startRef.current;
      progRef.current = Math.min(elapsed / STEP_MS, 1);

      if (progRef.current >= 1) {
        const next = activeRef.current + 1;
        if (next >= N) {
          fadingRef.current = true;
          setFading(true);
          resetTmRef.current = setTimeout(() => {
            activeRef.current = 0;
            progRef.current   = 0;
            cycleRef.current += 1;
            startRef.current  = performance.now();
            fadingRef.current = false;
            /* batch all content resets in one setState flush */
            setActive(0);
            setCycle(c => c + 1);
            setFading(false);
          }, FADE_MS);
        } else {
          activeRef.current = next;
          progRef.current   = 0;
          startRef.current  = performance.now();
          /* update content state immediately — React flushes this in the next paint */
          setActive(next);
        }
      }

      /* ring + track only need the tick */
      setTick(n => n + 1);
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resetTmRef.current);
    };
  }, []);

  const goTo = (idx) => {
    clearTimeout(resetTmRef.current);
    fadingRef.current = false;
    activeRef.current = idx;
    progRef.current   = 0;
    startRef.current  = performance.now();
    setActive(idx);
    setCycle(c => c + 1);
    setFading(false);
  };

  /* progress is read from ref — always current at render time */
  const progress = progRef.current;
  const CIRC     = 97.39;
  /* Track: spans from center of first node to center of last node.
     Each node is flex:1 so center of node i = (i + 0.5) / N × 100%.
     → left = 10%, right = 10% for N=5 */
  const trackEdge = `${(0.5 / N) * 100}%`;
  const { Visual } = STEPS[active];

  return (
    <section className="how" id="how">

      <div className="how-header">
        <div className="kicker">
          <span className="kicker-dot" />
          <span>Scan. Order. Served.</span>
        </div>
        <h2 className="display how-title">
          From scan<br />to <em>served.</em>
        </h2>
      </div>

      <div className={`how-stepper${fading ? ' fading' : ''}`}>

        {/* ── Nodes + track ── */}
        <div className="how-steps-row">
          {STEPS.map((s, i) => (
            <button
              key={s.num}
              className={`how-node${i === active ? ' active' : ''}${i < active ? ' done' : ''}`}
              aria-current={i === active ? 'step' : undefined}
              onClick={() => goTo(i)}
            >
              <div className="how-node-circle">
                <span className="how-node-num">{s.num}</span>
                <svg className="how-node-ring" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" />
                  {i === activeRef.current && (
                    <circle
                      cx="18" cy="18" r="15.5"
                      className="how-node-ring-fill"
                      style={{
                        strokeDasharray:  CIRC,
                        strokeDashoffset: CIRC - progress * CIRC,
                      }}
                    />
                  )}
                </svg>
              </div>
              <span className="how-node-title">{s.title}</span>
            </button>
          ))}

          <div className="how-track" style={{ left: trackEdge, right: trackEdge }}>
            <div
              className="how-track-fill"
              style={{ width: `${((activeRef.current + progress) / (N - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* ── Content panel ── */}
        <div className="how-panel">
          <div className="how-panel-visual" key={`v-${active}-${cycle}`}>
            <Visual />
          </div>
          <div className="how-panel-text" key={`t-${active}-${cycle}`}>
            <span className="how-panel-num">{STEPS[active].num}</span>
            <h3 className="display how-panel-title">{STEPS[active].title}</h3>
            <p className="how-panel-sub">{STEPS[active].sub}</p>
          </div>
        </div>

      </div>
    </section>
  );
}

const QR_CELLS = [
  1,1,1,1,1,1,1,
  1,0,0,0,0,0,1,
  1,0,1,0,1,0,1,
  1,0,0,1,0,0,1,
  1,0,1,0,1,0,1,
  1,0,0,0,0,0,1,
  1,1,1,1,1,1,1,
].map(Boolean);
